<script setup lang="ts">
import { REQUEST_STATUS } from '@/constants/api/requestStatus'

import { useCityStore } from '@/store/city/city'
import { useToggleStore } from '@/store/toggle/toggle'

import { AsyncDataKey } from '@/types/main'
import { NAME_PARENT_COMPONENT } from '@/types/products'

import ProductItem from '@/components/Product/ProductItem/ProductItem.vue'
import SkeletonProductPage from '@/components/Skeleton/SkeletonProductPage.vue'

const { $apiService, $metrics } = useNuxtApp()

const { menuByPointsEnabled, isLandingPageEnabled } = useToggleStore()
const {
  cityInfo: { domain },
} = useCityStore()

const route = useRoute()

const {
  menuByPointData,
  isAddressV5Selected,
  getAsyncDataKeyComputed,
  getAvailableProductsData,
  syncAddressWithV5Api,
} = useMenuByPoint()

const productSlug = computed(() => String(route.params.product))

// Ключ для fetch данных продукта на основе выбранного адреса
const productDataKey = getAsyncDataKeyComputed(
  `${AsyncDataKey.ONE_PRODUCT_DATA}-${productSlug.value}`,
)

const isAvailable = ref(true)

if (import.meta.client) {
  await callOnce(async () => {
    if (!menuByPointsEnabled) return

    // Синхронизируем адрес с V5 API меню
    await syncAddressWithV5Api()
  })
}

const {
  data: productInfo,
  error,
  status,
} = await useFetchData(
  async () => {
    if (menuByPointsEnabled) {
      const product = await fetchHandler(
        $apiService.category.fetchOneProductV5.bind(
          this,
          productSlug.value,
          menuByPointData.value,
        ),
      )

      // Если нет метода доставки или адреса, то возвращаем то что прислал бэк
      if (!product || !isAddressV5Selected.value) return product

      const productData = getAvailableProductsData(product)

      // Если у продукта нет доступных модификаций - значит весь продукт недоступен
      isAvailable.value = productData.isAvailable

      if (productData.isAvailable) {
        product.modifications = productData.modifications
      }

      return product
    }

    return fetchHandler(
      $apiService.category.fetchOneProduct.bind(this, productSlug.value),
    )
  },
  {
    withoutCache: true,
    key: productDataKey.value,
    watch: [productDataKey], // Обновляем продукт при изменении адреса
  },
)

if (error.value) showError(error.value)

watch(error, () => {
  if (!error.value) return

  showError(error.value)
})

const showSkeletonLoader = computed(() => {
  return [REQUEST_STATUS.idle, REQUEST_STATUS.pending].includes(status.value)
})

const productBreadcrumbs = computed(() => {
  if (!isLandingPageEnabled) return productInfo.value?.breadcrumbs

  return productInfo.value?.breadcrumbs?.filter((breadcrumb) => {
    return breadcrumb.href !== productInfo.value?.category.path
  })
})

usePopupFetch()

onMounted(() => {
  // Просмотр страницы блюда
  $metrics?.eventSendGoal({ ym: 'open_item_page' })
})
</script>

<template>
  <div class="base-container">
    <div v-if="productInfo" class="page__content">
      <app-head
        :title="productInfo?.seo?.seo_title"
        :seo="{
          title: productInfo?.seo?.seo_title,
          keywords: productInfo?.seo?.seo_keywords,
          description: productInfo?.seo?.seo_description,
        }"
      />

      <the-breadcrumbs
        v-if="productBreadcrumbs"
        :breadcrumbs="productBreadcrumbs"
        :domain="domain"
      />

      <product-item
        :product="productInfo"
        :parent-component="NAME_PARENT_COMPONENT.PAGE"
        :is-available="isAvailable"
      />
    </div>

    <skeleton-product-page v-else-if="showSkeletonLoader" />
  </div>
</template>

<style lang="scss" scoped>
.base-container {
  padding: 30px calc((100% - 1200px) / 2) 100px;
  @include media('>=portrait-phone', '<=desktop') {
    padding-right: 48px;
    padding-left: 48px;
  }

  @include media('<=portrait-phone') {
    padding-right: 20px;
    padding-left: 20px;
  }
}
</style>
